export const QTY_MAX = 999;

// Whole number from 1 to QTY_MAX, as a number or a string of digits.
export function isValidQuantity(value) {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 1 && value <= QTY_MAX;
  }
  if (typeof value !== 'string') return false;
  const text = value.trim();
  if (!/^\d+$/.test(text)) return false;
  const quantity = parseInt(text, 10);
  return quantity >= 1 && quantity <= QTY_MAX;
}

export function lineAmount(quantity, cost) {
  return Number(quantity) * Number(cost);
}

// From Application and bundle lines follow the order's services.
export function isQuantityLocked(row) {
  return Boolean(row.fromIPOrder) || row.bundleId !== 0;
}

// True when Save must be blocked: an editable row has a bad quantity or, when
// requireEquipment is set, no equipment picked.
export function hasInvalidLines(rows, requireEquipment) {
  return rows.some((row) => {
    if (isQuantityLocked(row)) return false;
    if (!isValidQuantity(row.quantity)) return true;
    return Boolean(requireEquipment) && !(row.handsetID > 0);
  });
}

export const DEFAULT_SAVE_ERROR =
  'Hardware not saved. Please try again or contact support.';
export const DELETE_FAILED_ERROR =
  'Line not deleted. Please try again or contact support.';

// A line the server already stores (new lines have id 0 until saved).
export function isSavedRow(row) {
  return Boolean(row) && row.hardwareID > 0 && row.hardwareOrderID > 0;
}

// An Error whose text is safe to show the user.
export function userError(message) {
  const error = new Error(message);
  error.userMessage = message;
  return error;
}

// Server text first, then our own text; transport and code errors never show.
export function saveErrorMessage(err) {
  const data = err && err.response && err.response.data;
  if (data && typeof data.message === 'string' && data.message) {
    return data.message;
  }
  if (err && typeof err.userMessage === 'string' && err.userMessage) {
    return err.userMessage;
  }
  return DEFAULT_SAVE_ERROR;
}

// Removes this exact row object; returns its old index or -1.
export function removeRowObject(rows, row) {
  const index = rows.indexOf(row);
  if (index > -1) rows.splice(index, 1);
  return index;
}

// Where this row is now: the same object, else (saved rows only) the reloaded
// copy with the same id, else -1.
export function findRowIndex(rows, row) {
  if (!row) return -1;
  const index = rows.indexOf(row);
  if (index > -1 || !isSavedRow(row)) return index;
  return rows.findIndex((other) => other.hardwareID === row.hardwareID);
}

// Puts a removed row back at its old index, or the end if rows shrank. Does
// nothing when the rows already show that line (a reload brought it back).
export function putRowBack(rows, row, index) {
  if (findRowIndex(rows, row) > -1) return;
  rows.splice(Math.max(0, Math.min(index, rows.length)), 0, row);
}

// What AddHardwareToOrder gets: Additional lines only, quantity as an integer.
export function additionalRowsPayload(rows) {
  return rows
    .filter((row) => !row.fromIPOrder)
    .map((row) =>
      Object.assign({}, row, {
        quantity: isValidQuantity(row.quantity)
          ? parseInt(row.quantity, 10)
          : row.quantity,
      })
    );
}

// What a companion costs when it comes with a main item: the related item's
// included price when the main item has an override, else its RRP.
export function companionCost(mainHandset, relatedHandset) {
  return mainHandset.hasOverride
    ? relatedHandset.priceWhenIncluded
    : relatedHandset.rrpEx;
}

function quantityOrZero(row) {
  return isValidQuantity(row.quantity) ? parseInt(row.quantity, 10) : 0;
}

// Keeps the companion lines of an always-include item (e.g. the PSU of a
// handset) at the total quantity of the main lines that pull it in. Returns
// what to change; rows are only read. Only companion lines ever change.
// Indexes are positions in `rows`; removeIndexes are highest first.
export function planCompanionSync(rows, handsets, relatedId, mainHandset) {
  const plan = { setQuantity: [], add: null, removeIndexes: [] };
  if (!relatedId || !mainHandset) return plan;
  const related = handsets.find((h) => h.handsetID === relatedId);
  if (!related) return plan;

  const mainIds = handsets
    .filter((h) => h.alwaysInclude && h.relatedHardwareID === relatedId)
    .map((h) => h.handsetID);
  let target = 0;
  let mainQuantityValid = true;
  const companions = [];
  rows.forEach((row, index) => {
    if (isQuantityLocked(row)) return;
    if (mainIds.indexOf(row.handsetID) > -1) {
      if (isValidQuantity(row.quantity)) target += quantityOrZero(row);
      else mainQuantityValid = false;
    } else if (row.handsetID === relatedId) {
      companions.push({ row, index, quantity: quantityOrZero(row) });
    }
  });
  // a blank or half-typed main quantity must not delete companions
  if (!mainQuantityValid) return plan;

  const total = companions.reduce((sum, c) => sum + c.quantity, 0);
  if (total < target) {
    const last = companions[companions.length - 1];
    if (!last) {
      const cost = companionCost(mainHandset, related);
      plan.add = {
        handsetID: relatedId,
        quantity: target,
        cost,
        lineTotal: lineAmount(target, cost),
      };
    } else {
      const quantity = last.quantity + (target - total);
      plan.setQuantity.push({
        index: last.index,
        quantity,
        lineTotal: lineAmount(quantity, last.row.cost),
      });
    }
  } else if (total > target) {
    let excess = total - target;
    for (let i = companions.length - 1; i >= 0 && excess > 0; i--) {
      const c = companions[i];
      if (excess >= c.quantity) {
        plan.removeIndexes.push(c.index);
        excess -= c.quantity;
      } else {
        const quantity = c.quantity - excess;
        plan.setQuantity.push({
          index: c.index,
          quantity,
          lineTotal: lineAmount(quantity, c.row.cost),
        });
        excess = 0;
      }
    }
    plan.removeIndexes.sort((a, b) => b - a);
  }
  return plan;
}

// Applies a plan: quantity and amount are set together, removals are
// dispatched highest index first without waiting (each removes its row at
// once), then the new companion line is added. Returns the removal promises.
export function applyCompanionPlan(plan, rows, { addRow, deleteRowAt }) {
  plan.setQuantity.forEach(({ index, quantity, lineTotal }) => {
    rows[index].quantity = quantity;
    rows[index].lineTotal = lineTotal;
  });
  const removals = plan.removeIndexes.map((index) => deleteRowAt(index));
  if (plan.add) addRow(plan.add);
  return removals;
}
